import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import { FieldModel } from 'shared/models/form';
import { validateIsEmpty, validateWidgetUrl } from 'shared/entities/validator';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { IRootStore } from 'shared/entities/store/rootStore';
import {
  ChannelKind,
  ChannelWidgetServer,
  CreateChannelError,
  mapCreateChannelErrorToMessage
} from 'shared/entities/channels';
import { apiUrls } from 'shared/entities/domain';
import { WidgetChannelModel } from 'shared/models/channel/widget';
import { REG_EXP_PROTOCOL } from 'shared/entities/regExp/url';

type PrivateFields = '_channel';

export class WidgetChannelCreationStore {
  readonly kind: ChannelKind.WIDGET = ChannelKind.WIDGET;
  readonly url: FieldModel = new FieldModel<string>('', [
    validateIsEmpty,
    validateWidgetUrl
  ]);
  readonly creationStage: LoadingStageModel = new LoadingStageModel();
  readonly rootStore: IRootStore;
  private _channel: WidgetChannelModel | null = null;

  constructor({ rootStore }: { rootStore: IRootStore }) {
    this.rootStore = rootStore;

    makeObservable<WidgetChannelCreationStore, PrivateFields>(this, {
      _channel: observable.ref,

      isError: computed,
      channel: computed,

      reset: action.bound
    });
  }

  get isError(): boolean {
    return this.url.isError;
  }

  get channel(): WidgetChannelModel | null {
    return this._channel;
  }

  validate(): boolean {
    this.url.validate();

    return this.isError;
  }

  reset(): void {
    this._channel = null;

    this.url.reset();
  }

  create = async (): Promise<BaseResponse<{ channelId: string }>> => {
    if (this.creationStage.isLoading) {
      return {
        isError: true
      };
    }

    const isError = this.validate();

    if (isError) {
      return {
        isError: true
      };
    }

    this.creationStage.loading();

    const response = await this.rootStore.networkStore.api<
      {
        channel: ChannelWidgetServer;
      },
      CreateChannelError
    >(apiUrls.CHANNELS_WIDGET_CREATE, {
      method: 'POST',
      data: {
        url: this._normalizeUrl(this.url.value)
      },
      errorsMap: mapCreateChannelErrorToMessage,
      showExpectedError: false
    });

    if (response.isError) {
      this.creationStage.error();

      if (
        response.data?.code &&
        Object.values(CreateChannelError).includes(response.data?.code)
      ) {
        this.url.setError(mapCreateChannelErrorToMessage(response.data.code));
      }

      return {
        isError: true
      };
    }

    const channel = WidgetChannelModel.fromJson(
      response.data.channel,
      this.rootStore
    );

    runInAction(() => {
      if (channel.kind == ChannelKind.WIDGET) {
        this._channel = channel;
      }
    });

    this.rootStore.channelsStore.addCreatedChannel(channel);
    this.creationStage.success();

    return {
      isError: false,
      data: {
        channelId: channel.id
      }
    };
  };

  private _normalizeUrl(url: string): string {
    const trimmedUrl = url.trim();

    if (REG_EXP_PROTOCOL.test(trimmedUrl)) {
      return trimmedUrl;
    }

    return `https://${trimmedUrl}`;
  }
}
